import { useEffect, useState } from "react";

import { useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { EditorCellProps } from "src/base-components/EditorTable/types";
import { DecisionTableField } from "src/clients/flow-api";
import {
  HIGHLIGHT_CLASSES,
  useIsFieldHighlighted,
} from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useFactorContext } from "src/scorecardNode/context";
import { FormShape } from "src/scorecardNode/types";
import { validateWeight } from "src/scorecardNode/utils";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

type Props<T> = EditorCellProps<T, DecisionTableField>;

const FLOAT_PATTERN = /^(\d+(\.\d*)?|\.\d+)$/;

export const WeightEditor = <T,>({ state }: Props<T>) => {
  const { equalWeights, totalFactors, index: factorIndex } = useFactorContext();
  const { watch, setValue, getValues } = useFormContext<FormShape>();
  const field = `factors.${factorIndex}.weight` as const;
  const fieldName = `${field}.value` as const;
  const fieldId = getValues(`${field}.id`);
  const isSearchHighlighted = useIsFieldHighlighted(fieldId);
  const value = watch(fieldName);
  const [ref] =
    useHighlightedScrollIntoView<HTMLInputElement>(isSearchHighlighted);
  const [userValue, setUserValue] = useState(value);

  useEffect(() => {
    if (!state.isEditing && !state.isSelected) {
      setUserValue(value);
    }
  }, [value, state.isEditing, state.isSelected]);

  useEffect(() => {
    if (ref.current && (state.isEditing || state.isSelected)) {
      ref.current.focus();
    }
  }, [state]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value;

    if (value.length === 0 || FLOAT_PATTERN.test(e.target.value)) {
      setUserValue(e.target.value);
    }
  };

  const handleBlur = () => {
    const validatedWeight = validateWeight(userValue);
    setUserValue(validatedWeight);
    if (validatedWeight !== value) {
      setValue(fieldName, validatedWeight);
    }
  };

  if (equalWeights) {
    return (
      <p className="w-full px-2 pt-3 font-fira-code text-gray-500">
        1/{totalFactors}
      </p>
    );
  }

  return (
    <>
      <input
        ref={ref}
        className={twJoin(
          "ml-[1px] h-fit border-0 p-2",
          "w-full font-code-12 outline-0 ring-0 focus:outline-0 focus:ring-0",
          state.readonly && "cursor-not-allowed bg-gray-100",
          isSearchHighlighted && HIGHLIGHT_CLASSES,
        )}
        name="weight"
        placeholder="e.g. 5"
        readOnly={state.readonly}
        type="text"
        value={userValue}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      <span className="h-fit py-2 pr-2 font-fira-code text-gray-400">%</span>
    </>
  );
};
